// commonsku art on live accounts (Stephanie + reps, Jake go 2026-11-02).
//
//   • ClientArtLibrary — the account's art library (client_brand_assets): what
//     the client also sees in the portal's Brand Assets page.
//   • CommonskuArtList — the account's OLD commonsku art (artwork, client files,
//     proofs) from the CS Import archive, newest first, with search + kind
//     filter. On the account page each file has "Add to this client's art";
//     in the quote item's art picker (projectId given) it is "Add & use",
//     which also puts the file in the project's Files and attaches it.
//
// Files only — never commonsku document contents. Staff-only routes
// (apps/api/src/routes/client-cs-art.ts).
import { useDeferredValue, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, ExternalLink, History, Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { formatShortDate } from "@/lib/production";

type ArtKind = "artwork" | "client_file" | "proof";

interface CsArtFile {
  id: string;
  kind: ArtKind;
  name: string;
  mime_type: string | null;
  size_bytes: number | null;
  date: string | null;
  job_number: number | null;
  project_name: string | null;
  title: string | null;
  location: string | null;
  imprint: string | null;
  color: string | null;
  added_file_id: string | null;
  is_image: boolean;
  url: string | null;
  download_url: string | null;
}

interface CsArtResponse {
  ready: boolean;
  source: { from: "own" | "parent"; archive_name: string; parent_name: string | null; art_count: number } | null;
  total: number;
  counts: Partial<Record<ArtKind, number>>;
  files: CsArtFile[];
}

interface ClientArtFile {
  asset_id: string;
  tag: string;
  added_at: string;
  file_id: string;
  name: string;
  mime_type: string;
  size_bytes: number;
  from_commonsku: boolean;
  added_by: string | null;
  is_image: boolean;
  url: string | null;
  download_url: string | null;
}

const KIND_LABEL: Record<ArtKind, string> = { artwork: "Artwork", client_file: "Client file", proof: "Proof" };
const KIND_FILTERS: Array<{ id: ArtKind | ""; label: string }> = [
  { id: "", label: "All" },
  { id: "artwork", label: "Artwork" },
  { id: "client_file", label: "Client files" },
  { id: "proof", label: "Proofs" },
];

function typeBadge(name: string, mime: string | null): string {
  const ext = /\.([a-z0-9]{1,5})$/i.exec(name)?.[1];
  return (ext ?? mime?.split("/")[1] ?? "file").toUpperCase();
}

function Thumb(props: { isImage: boolean; url: string | null; name: string; mime: string | null; tall?: boolean }) {
  return (
    <div className={cn("flex items-center justify-center overflow-hidden bg-cream-100", props.tall ? "h-40" : "h-32")}>
      {props.isImage && props.url ? (
        <img src={props.url} alt={props.name} loading="lazy" className="h-full w-full object-contain p-2" />
      ) : (
        <span className="font-display text-sm font-bold tracking-wide text-brown-300">{typeBadge(props.name, props.mime)}</span>
      )}
    </div>
  );
}

function FileLinks(props: { url: string | null; downloadUrl: string | null }) {
  if (!props.url && !props.downloadUrl) return <span className="text-sm text-brown-300">File not available</span>;
  return (
    <span className="flex items-center gap-3 text-sm">
      {props.url && (
        <a href={props.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-ink underline-offset-4 hover:underline">
          <ExternalLink className="h-3.5 w-3.5" aria-hidden /> Open
        </a>
      )}
      {props.downloadUrl && (
        <a href={props.downloadUrl} className="inline-flex items-center gap-1 font-medium text-ink underline-offset-4 hover:underline">
          <Download className="h-3.5 w-3.5" aria-hidden /> Download
        </a>
      )}
    </span>
  );
}

// ─── The account's art library ─────────────────────────────────────────────

export function ClientArtLibrary(props: { clientId: string }) {
  const { clientId } = props;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["clients", clientId, "art"],
    queryFn: () => api<{ files: ClientArtFile[] }>(`/clients/${clientId}/art`),
  });
  const remove = useMutation({
    mutationFn: (assetId: string) => api(`/clients/${clientId}/art/${assetId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients", clientId, "art"] });
      void qc.invalidateQueries({ queryKey: ["clients", clientId, "commonsku-art"] });
    },
  });
  const files = q.data?.files ?? [];

  return (
    <section>
      <h2 className="text-lg font-semibold text-brown-500">
        This client's art <span className="text-brown-300">({files.length})</span>
      </h2>
      <p className="mt-1 text-sm text-brown-400">
        The client's art library: what they uploaded in their portal and what you added from commonsku. The client sees
        these in their portal (Brand Assets).
      </p>
      {q.isLoading ? (
        <p className="mt-4 text-sm text-brown-300">Loading art…</p>
      ) : q.isError ? (
        <p className="mt-4 text-sm text-rust-600">Couldn't load this client's art. Refresh to try again.</p>
      ) : files.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-brown-100 px-6 py-8 text-center text-sm text-brown-400">
          No art in the library yet. Add files from commonsku below.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {files.map((f) => (
            <div key={f.asset_id} className="overflow-hidden rounded-lg border border-brown-100 bg-white">
              <Thumb isImage={f.is_image} url={f.url} name={f.name} mime={f.mime_type} />
              <div className="space-y-1.5 p-3">
                <div className="truncate text-[15px] font-semibold text-brown-500" title={f.name}>{f.name}</div>
                <div className="text-sm text-brown-300">
                  {f.from_commonsku ? "From commonsku" : "Uploaded by the client"} · {formatShortDate(f.added_at.slice(0, 10), true)}
                </div>
                <FileLinks url={f.url} downloadUrl={f.download_url} />
                {f.from_commonsku && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(f.asset_id)}
                    disabled={remove.isPending}
                    className="inline-flex items-center gap-1 text-sm text-brown-300 underline-offset-4 hover:text-rust-600 hover:underline disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden /> Remove from client's art
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {remove.isError && <p className="mt-2 text-sm text-rust-600">Couldn't remove it. Try again.</p>}
    </section>
  );
}

// ─── The account's old commonsku art ────────────────────────────────────────

export function CommonskuArtList(props: {
  /** The account page: "Add to this client's art". */
  clientId?: string;
  /** The art picker: "Add & use" on this project's account. */
  projectId?: string;
  selectedFileId?: string | null;
  onUse?: (fileId: string, name: string, imageUrl: string | null) => void;
  /** Pictures only — the item window's picture box (Stephanie 2026-11-04). */
  imagesOnly?: boolean;
}) {
  const { clientId, projectId, selectedFileId, onUse, imagesOnly = false } = props;
  const inPicker = Boolean(projectId);
  const base = projectId ? `/projects/${projectId}/commonsku-art` : `/clients/${clientId}/commonsku-art`;
  const listKey = projectId ? ["project", projectId, "commonsku-art"] : ["clients", clientId, "commonsku-art"];
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<ArtKind | "">("");
  const q = useDeferredValue(search.trim());

  const list = useInfiniteQuery({
    queryKey: [...listKey, q, kind, imagesOnly],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ offset: String(pageParam) });
      if (q.length >= 2) params.set("q", q);
      if (kind) params.set("kind", kind);
      if (imagesOnly) params.set("images", "1");
      return api<CsArtResponse>(`${base}?${params.toString()}`);
    },
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.files.length, 0);
      return last.files.length > 0 && loaded < last.total ? loaded : undefined;
    },
    enabled: Boolean(clientId || projectId),
  });

  const add = useMutation({
    mutationFn: (f: CsArtFile) =>
      api<{ file_id: string; added: boolean; image_url?: string | null }>(
        projectId ? `/projects/${projectId}/commonsku-art/${f.id}/use` : `/clients/${clientId}/commonsku-art/${f.id}/add`,
        { method: "POST", body: JSON.stringify({}) },
      ).then((r) => ({ ...r, name: f.name })),
    onSuccess: ({ file_id, name, image_url }) => {
      void qc.invalidateQueries({ queryKey: listKey });
      if (clientId) void qc.invalidateQueries({ queryKey: ["clients", clientId, "art"] });
      if (projectId) void qc.invalidateQueries({ queryKey: ["project", projectId, "files"] });
      onUse?.(file_id, name, image_url ?? null);
    },
  });

  const first = list.data?.pages[0];
  const files = list.data?.pages.flatMap((p) => p.files) ?? [];
  const source = first?.source ?? null;

  // In the picker, stay out of the way when there's nothing from commonsku.
  if (inPicker && (!first || !first.ready || !source || source.art_count === 0)) return null;

  return (
    <section className={inPicker ? "mt-8" : undefined}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-brown-500">
            <History className="h-4 w-4 text-brown-300" aria-hidden />
            From commonsku {first?.ready && <span className="text-brown-300">({first.total.toLocaleString()})</span>}
          </h2>
          <p className="mt-1 text-sm text-brown-400">
            {source?.from === "parent"
              ? `This client's old artwork, client files and proofs, from the main account ${source.parent_name ?? source.archive_name} in commonsku.`
              : "This client's old artwork, client files and proofs from commonsku, newest first."}{" "}
            {imagesOnly
              ? "Pictures only. Use puts it in this client's art and this project's Files and makes it the item's first picture."
              : inPicker
              ? "Add & use puts it in this client's art and this project's Files and attaches it."
              : "Nothing is shared until you add it. The client can see art you add here in their portal (Brand Assets), file name included."}
          </p>
        </div>
        {first?.ready && source && (
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brown-300" aria-hidden />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or job #"
              className="pl-9 text-sm"
              aria-label="Search commonsku art"
            />
          </div>
        )}
      </div>

      {first?.ready && source && !imagesOnly && (
        <div className="mt-3 flex flex-wrap gap-2">
          {KIND_FILTERS.map((k) => {
            const n = k.id ? (first.counts[k.id] ?? 0) : source.art_count;
            return (
              <button
                key={k.id || "all"}
                type="button"
                onClick={() => setKind(k.id)}
                className={cn(
                  "rounded-full border px-3 py-1 text-sm transition-colors",
                  kind === k.id ? "border-ink bg-ink text-white" : "border-rule bg-white text-brown-400 hover:bg-cream-100",
                )}
              >
                {k.label} ({n.toLocaleString()})
              </button>
            );
          })}
        </div>
      )}

      {list.isLoading ? (
        <p className="mt-4 text-sm text-brown-300">Loading commonsku art…</p>
      ) : list.isError ? (
        <p className="mt-4 text-sm text-rust-600">Couldn't load the commonsku art. Refresh to try again.</p>
      ) : !first?.ready ? (
        <div className="mt-4 rounded-lg border border-dashed border-brown-100 px-6 py-8 text-center text-sm text-brown-400">
          The commonsku archive isn't loaded yet.
        </div>
      ) : !source ? (
        <div className="mt-4 rounded-lg border border-dashed border-brown-100 px-6 py-8 text-center text-sm text-brown-400">
          No commonsku art for this account. It isn't linked to a commonsku client, or that client's files aren't in
          the archive yet.
        </div>
      ) : files.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-brown-100 px-6 py-8 text-center text-sm text-brown-400">
          {q.length >= 2 || kind ? "Nothing matches. Try another search." : "No commonsku art for this account yet."}
        </div>
      ) : (
        <div className={cn("mt-4 grid gap-4", inPicker ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6")}>
          {files.map((f) => {
            const added = f.added_file_id !== null;
            const selected = inPicker && added && f.added_file_id === selectedFileId;
            const busy = add.isPending && add.variables?.id === f.id;
            const placement = [f.title, f.location, f.imprint, f.color].filter(Boolean).join(" · ");
            return (
              <div key={f.id} className={cn("flex flex-col overflow-hidden rounded-lg border bg-white", selected ? "border-ink ring-2 ring-ink" : "border-brown-100")}>
                <Thumb isImage={f.is_image} url={f.url} name={f.name} mime={f.mime_type} />
                <div className="flex flex-1 flex-col gap-1.5 p-3">
                  <div className="truncate text-[15px] font-semibold text-brown-500" title={f.name}>{f.name}</div>
                  <div className="text-sm text-brown-300">
                    {KIND_LABEL[f.kind]}
                    {f.date ? ` · ${formatShortDate(f.date, true)}` : ""}
                  </div>
                  {(f.job_number || f.project_name) && (
                    <div className="truncate text-sm text-brown-400" title={`${f.job_number ? `#${f.job_number} ` : ""}${f.project_name ?? ""}`}>
                      {f.job_number ? `#${f.job_number}` : ""} {f.project_name ?? ""}
                    </div>
                  )}
                  {placement && <div className="truncate text-sm text-brown-300" title={placement}>{placement}</div>}
                  <FileLinks url={f.url} downloadUrl={f.download_url} />
                  <div className="mt-auto pt-2">
                    {inPicker ? (
                      <Button size="sm" variant={selected ? "outline" : "primary"} className="w-full" disabled={busy || selected} onClick={() => add.mutate(f)}>
                        {selected ? <><Check className="h-3.5 w-3.5" /> In use</> : busy ? "Adding…" : added ? "Use" : "Add & use"}
                      </Button>
                    ) : added ? (
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-brown-400">
                        <Check className="h-3.5 w-3.5" aria-hidden /> In this client's art
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full" disabled={busy} onClick={() => add.mutate(f)}>
                        {busy ? "Adding…" : "Add to this client's art"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {add.isError && <p className="mt-2 text-sm text-rust-600">Couldn't add that file. Try again.</p>}
      {list.hasNextPage && (
        <div className="mt-4 text-center">
          <Button size="sm" variant="outline" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
            {list.isFetchingNextPage ? "Loading…" : "Show more"}
          </Button>
        </div>
      )}
    </section>
  );
}
